import { Button, Col, Form, Row } from 'antd';
import isNil from 'lodash/isNil';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import DefaultDrawer from 'components/composites/DefaultDrawer';
import { SEARCH_PARAMS } from 'constants/search-params';
import { FilterSetName } from 'store/modules/routerUtils/types';

import { getFilterSetFromSearchParams } from './getFilterSetFromSearchParams';

interface Props {
  filterSetName: FilterSetName;
  children: React.ReactElement;
}

function BackOfficeFilterDrawer({
  filterSetName,
  children
}: Props): JSX.Element | null {
  const [searchParams, setSearchParams] = useSearchParams();
  const drawerFilter = searchParams.get(SEARCH_PARAMS.BACK_OFFICE.DRAWER_FILTER);
  const filterSet = useMemo(
    () => getFilterSetFromSearchParams(searchParams, filterSetName),
    [searchParams, filterSetName]
  );

  const [form] = Form.useForm();

  const visible = useMemo(
    () => drawerFilter === filterSetName,
    [drawerFilter, filterSetName]
  );

  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible, form]);

  const handleClose = useCallback(() => {
    form.resetFields();
    setSearchParams((prev) => {
      const newSearchParams = new URLSearchParams(prev);
      newSearchParams.delete(SEARCH_PARAMS.BACK_OFFICE.DRAWER_FILTER);
      return newSearchParams;
    });
  }, [form, setSearchParams]);

  const handleSubmitAndClose = useCallback(
    (values: Record<string, string | string[] | number | number[] | null | undefined>) => {
      setSearchParams((prev) => {
        const newSearchParams = new URLSearchParams(prev);
        // Close drawer
        newSearchParams.delete(SEARCH_PARAMS.BACK_OFFICE.DRAWER_FILTER);
        // Let filters take effect
        newSearchParams.delete(SEARCH_PARAMS.CURRENT_PAGE);
        for (const [key, value] of Object.entries(values)) {
          const paramKey = `filter.${filterSetName}.${key}`;
          const paramKeyBracket = `${paramKey}[]`;
          if (isNil(value)) {
            newSearchParams.delete(paramKey);
            newSearchParams.delete(paramKeyBracket);
          } else if (Array.isArray(value)) {
            newSearchParams.delete(paramKey);
            newSearchParams.delete(paramKeyBracket);
            value.forEach((v) => newSearchParams.append(paramKeyBracket, String(v)));
          } else {
            newSearchParams.delete(paramKeyBracket);
            newSearchParams.set(paramKey, String(value));
          }
        }
        return newSearchParams;
      });
    },
    [filterSetName, setSearchParams]
  );

  const handleClick = useCallback(() => {
    form
      .validateFields()
      .then((values) => {
        handleSubmitAndClose(values);
      })
      .catch(() => {
        // Ignoring promise rejection
      });
  }, [form, handleSubmitAndClose]);

  const handleClearFiltersClick = useCallback(() => {
    const fieldsValue = form.getFieldsValue();

    const nextFieldsValue = Object.keys(fieldsValue).reduce(
      (accumulator: Record<string, undefined>, currentValue: string) => {
        accumulator[currentValue] = undefined;
        return accumulator;
      },
      {}
    );

    form.setFieldsValue(nextFieldsValue);
    handleClick();
  }, [form, handleClick]);

  const childrenWithFormProps = useMemo(
    () =>
      React.cloneElement(children, {
        form,
        initialValues: filterSet
      }),
    [children, filterSet, form]
  );

  return (
    <DefaultDrawer
      id={filterSetName}
      placement="left"
      onClose={handleClose}
      visible={visible}
      width={300}
      title="Filter"
      footer={
        <Row gutter={24}>
          <Col span={12}>
            <Button block onClick={handleClearFiltersClick}>
              CLEAR FILTERS
            </Button>
          </Col>
          <Col span={12}>
            <Button block onClick={handleClick} type="primary">
              APPLY FILTERS
            </Button>
          </Col>
        </Row>
      }
    >
      {childrenWithFormProps}
    </DefaultDrawer>
  );
}

export default memo(BackOfficeFilterDrawer);
